import { User, BusinessUserStats, getBusinessUserStats, useUpdateUserNotificationsMutation, useSetUserActivationMutation } from "../../utils/api/endpoints/usersApi";
import CloseButton from "../buttons/CloseButton";
import QuickViewModal from "../modal/QuickViewModal";
import { PropsWithChildren, useEffect, useState } from "react";
import PillList from "../pills/PillList";
import LinkIcon from "../../assets/icons/LinkIcon";
import LocationIcon from "../../assets/icons/LocationIcon";
import { useNavigate } from "react-router-dom";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import useAllUsers from "../../utils/hooks/user/useAllUsers";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { getResearcherAchievements, listResearcherCertifications } from "../../utils/api/endpoints/researcher/profile";
import ToggleRow from "../forms/inputs/ToggleRow";
import toast from "react-hot-toast";

const BUSINESS_ROLES = [
  UserRole.BUSINESS,
  UserRole.DEVELOPER,
  UserRole.BUSINESS_MANAGER,
  UserRole.BUSINESS_ADMINISTRATOR
];

const UserQuickViewSection = ({
  title,
  children,
  disableBorder
}: PropsWithChildren<{ title: string; disableBorder?: boolean }>) => (
  <article
    className={
      "mb-6" +
      (!disableBorder ? " border-t border-ctb-grey-300 pt-6" : "")
    }
  >
    <h2 className="mb-4 text-[18px] font-bold" style={{ color: "#010D2C" }}>{title}</h2>
    <span>{children}</span>
  </article>
);

const getInitials = (name: string) => {
  if (!name) return "";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getAchievementIcon = (achievementName: string) => {
  const name = achievementName?.toLowerCase() || '';
  if (name.includes('report') || name.includes('geek')) {
    return (
      <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 6a2 2 0 114 0 2 2 0 01-4 0zm2 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
      </svg>
    );
  } else if (name.includes('critical') || name.includes('hunter')) {
    return (
      <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
      </svg>
    );
  } else if (name.includes('reliable') || name.includes('reporter')) {
    return (
      <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
    );
  } else if (name.includes('dedicated') || name.includes('tester')) {
    return (
      <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
    );
  } else if (name.includes('master')) {
    return (
      <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 2L3 7v11a1 1 0 001 1h12a1 1 0 001-1V7l-7-5zM8 15a1 1 0 100-2 1 1 0 000 2zm4 0a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
      </svg>
    );
  } else if (name.includes('multi') || name.includes('domain') || name.includes('expert')) {
    return (
      <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
      </svg>
    );
  } else {
    // Default icon
    return (
      <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
      </svg>
    );
  }
};


const StatCard = ({ label, value }: { label: string; value: string | number }) => (
  <div className="flex flex-col rounded-xl p-4" style={{ backgroundColor: "#F8FAFF", border: "1px solid #E2EAF6" }}>
    <span className="text-2xl font-bold" style={{ color: "#010D2C" }}>{value}</span>
    <span className="mt-1 text-xs font-medium text-gray-500">{label}</span>
  </div>
);

const UserQuickView = ({
  user,
  onClose
}: {
  user?: User;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const { remindToCreateProgram } = useAllUsers();
  const { role: currentUserRole } = useUserCredentials();

  const [achievements, setAchievements] = useState<any[]>([]);
  const [certs, setCerts] = useState<any[]>([]);
  const [businessStats, setBusinessStats] = useState<BusinessUserStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [updateUserNotifications] = useUpdateUserNotificationsMutation();
  const [setUserActivation] = useSetUserActivationMutation();

  useEffect(() => {
    if (user && user.role === UserRole.RESEARCHER) {
      Promise.all([
        getResearcherAchievements(user.id),
        listResearcherCertifications(user.id)
      ])
        .then(([a, c]) => {
          setAchievements((a.achievements || []).filter((ach: any) => ach.unlocked));
          setCerts(c.certifications || []);
        })
        .catch(console.error);
    }
  }, [user]);

  useEffect(() => {
    if (user && BUSINESS_ROLES.includes(user.role as UserRole)) {
      setBusinessStats(null);
      setStatsLoading(true);
      getBusinessUserStats(user.id)
        .then(stats => setBusinessStats(stats))
        .catch(console.error)
        .finally(() => setStatsLoading(false));
    } else {
      setBusinessStats(null);
    }
  }, [user]);

  const handleNotificationToggle = async (status: boolean) => {
    if (!user) return;
    if (user.role === UserRole.ADMIN && currentUserRole !== UserRole.ADMIN) {
      toast.error("Unauthorized to change notifications for Admin users");
      return;
    }
    const loader = toast.loading("Updating notifications...");
    try {
      await updateUserNotifications({ userId: user.id, status }).unwrap();
      toast.success(`Notifications ${status ? "enabled" : "disabled"}`);
    } catch (err) {
      toast.error("Failed to update notifications");
    } finally {
      toast.remove(loader);
    }
  };

  const handleSOWToggle = async (isRequired: boolean) => {
    if (!user) return;
    const loader = toast.loading("Updating SOW requirement...");
    try {
      await setUserActivation({ userId: user.id, sowExempt: !isRequired }).unwrap();
      toast.success(`SOW requirement ${isRequired ? "enabled" : "disabled"}`);
    } catch (err) {
      toast.error("Failed to update SOW requirement");
    } finally {
      toast.remove(loader);
    }
  };

  if (!user) return <QuickViewModal enabled={false} onClick={onClose} />;

  const isBusinessRole = BUSINESS_ROLES.includes(user.role as UserRole);

  const displayName = user.displayName || user.username || "Unknown User";
  const initials = getInitials(displayName);

  return (
    <QuickViewModal
      enabled={true}
      onClick={onClose}
      className="md:!w-[600px] !p-0 overflow-y-auto"
    >
      <div className="flex min-h-full flex-col">
        {/* Dark Navy Header - Negative margin to pull it flush with parent p-5 if any */}
        <div className="relative h-36 w-full rounded-tl-lg" style={{ backgroundColor: "#010D2C" }}>
          <CloseButton
            iconClassName="h-6 w-6 fill-white"
            buttonClassName="absolute right-6 top-6 z-50 rounded-full bg-white/10 p-1 hover:bg-white/20 transition"
            onClick={onClose}
          />

          {/* Overlapping Profile Picture */}
          <div className="absolute -bottom-10 left-8 flex h-24 w-24 items-center justify-center rounded-full border-4 border-white object-cover"
            style={{ backgroundColor: "#58CC02", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)" }}>
            {user.profilePicture ? (
              <img src={user.profilePicture} alt={displayName} className="h-full w-full rounded-full object-cover bg-white" />
            ) : (
              <span className="text-2xl font-bold text-white tracking-widest">{initials}</span>
            )}
          </div>
        </div>

        {/* Padding applied here instead of outer wrapper */}
        <div className="px-8 pt-16 pb-8 flex-grow">
          {/* Name and Action Row */}
          <div className="flex items-start justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: "#010D2C" }}>{displayName}</h1>
              <p className="mt-1 text-sm font-medium" style={{ color: "#58CC02" }}>
                @{user.username}
              </p>
              {user.country && (
                <p className="mt-2 flex items-center gap-1 text-sm text-gray-500">
                  <LocationIcon className="h-4 w-4 fill-gray-400" /> {user.country}
                </p>
              )}
            </div>

            <div className="flex gap-2">
              {user.role === UserRole.BUSINESS &&
                ([UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER] as UserRole[]).includes(currentUserRole!) && (
                  <button
                    onClick={() => remindToCreateProgram(user.id)}
                    className="rounded-md border px-4 py-2 text-sm font-medium transition hover:bg-gray-50"
                    style={{ borderColor: "#010D2C", color: "#010D2C" }}
                  >
                    Send Program Reminder
                  </button>
                )}

              {user.role === UserRole.RESEARCHER && (
                <button
                  onClick={() => {
                    navigate(`/dashboard/users/researcher-profile/${user.id}`);
                    setTimeout(() => {
                      const el = document.getElementById('dashboard-main-scroll');
                      if (el) el.scrollTop = 0;
                      try { window.scrollTo({ top: 0, behavior: 'auto' }); } catch { }
                    }, 0);
                  }}
                  className="rounded-md border px-4 py-2 text-sm font-medium transition hover:bg-gray-50"
                  style={{ borderColor: "#010D2C", color: "#010D2C" }}
                >
                  View Full Profile
                </button>
              )}
            </div>
          </div>

          <div>
            {/* Business / Sub-role: Company Overview Stats */}
            {isBusinessRole && (businessStats || statsLoading) && (
              <UserQuickViewSection title="Company Overview" disableBorder>
                {statsLoading ? (
                  <p className="text-sm text-gray-400">Loading stats…</p>
                ) : businessStats ? (
                  <div className="space-y-4">
                    {businessStats.companyName && (
                      <div className="flex items-center gap-3 rounded-xl px-4 py-3" style={{ backgroundColor: "#010D2C" }}>
                        <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="#58CC02" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className="text-sm font-semibold text-white">{businessStats.companyName}</span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <StatCard label="Total Programs" value={businessStats.totalPrograms} />
                      <StatCard label="Active Programs" value={businessStats.activePrograms} />
                      <StatCard label="Total Users" value={businessStats.totalUsers} />
                      <StatCard label="Total Reports" value={businessStats.totalReports} />
                      <StatCard label="Pentest Reports" value={businessStats.totalPentestReports} />
                    </div>
                  </div>
                ) : null}
              </UserQuickViewSection>
            )}

            {user.about && (
              <UserQuickViewSection title="About" disableBorder={!isBusinessRole}>
                <p className="text-[15px] leading-relaxed text-gray-600">
                  {user.about}
                </p>
              </UserQuickViewSection>
            )}

            {user.role !== UserRole.RESEARCHER ? (
              <>
                {user.skills && user.skills.length > 0 && (
                  <UserQuickViewSection title="Skills">
                    <PillList
                      items={user.skills}
                      pillClassName="px-4 py-1.5 text-sm font-medium"
                      className="gap-3"
                      pillBuilder={item => (
                        <span style={{ backgroundColor: "#dcfce7", color: "#166534" }} className="rounded-full px-4 py-1.5 flex items-center justify-center">
                          {item}
                        </span>
                      )}
                    />
                  </UserQuickViewSection>
                )}

                {user.links && user.links.length > 0 && (
                  <UserQuickViewSection title="Links">
                    <div className="flex flex-col gap-3">
                      {user.links.map((link, idx) => (
                        <a
                          key={idx}
                          href={link.startsWith('http') ? link : `https://${link}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex w-fit items-center gap-3 rounded-md px-4 py-2 text-sm font-medium transition"
                          style={{ backgroundColor: "#F1F5F9", color: "#010D2C" }}
                        >
                          <LinkIcon className="h-4 w-4 fill-gray-500" />
                          <span className="truncate hover:underline" style={{ maxWidth: "300px" }}>
                            {link}
                          </span>
                          <svg className="ml-2 h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                            <polyline points="15 3 21 3 21 9" />
                            <line x1="10" y1="14" x2="21" y2="3" />
                          </svg>
                        </a>
                      ))}
                    </div>
                  </UserQuickViewSection>
                )}
              </>
            ) : (
              <>
                {certs.length > 0 && (
                  <UserQuickViewSection title="Certifications">
                    <div className="space-y-4">
                      {certs.slice(0, 3).map((c: any, index: number) => (
                        <div
                          key={c.certification_id || c.id || index}
                          className="rounded-xl bg-[#F9FAFB] p-4 flex items-center gap-4"
                        >
                          <div className="w-12 h-12 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-blue-600 shadow-sm flex-shrink-0">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="8" r="4" />
                              <path d="M8 14l-2 6 6-3 6 3-2-6" />
                            </svg>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-[#010D2C] leading-snug truncate">
                              {c.certification_name || "Certification"}
                            </div>
                            <div className="mt-1 flex items-center text-xs text-gray-600 flex-wrap">
                              <span>{c.issuing_organization || "Organization"}</span>
                              <span className="mx-2 text-gray-400">•</span>
                              <span>
                                Issued {c.issue_date
                                  ? new Date(c.issue_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                                  : "Unknown"}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </UserQuickViewSection>
                )}

                {achievements.length > 0 && (
                  <UserQuickViewSection title="Achievements">
                    <div className="space-y-4">
                      {achievements.slice(0, 3).map((a: any, index: number) => (
                        <div key={a.key || index} className="bg-gray-50 rounded-lg p-4 border border-gray-100 transition-shadow">
                          <div className="flex items-start justify-between mb-3">
                            <div className="w-10 h-10 rounded-full border-2 border-blue-500 flex items-center justify-center flex-shrink-0">
                              {getAchievementIcon(a.name)}
                            </div>
                            <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                              Earned
                            </span>
                          </div>
                          <div className="mb-2">
                            <h4 className="text-sm font-semibold text-gray-900 mb-1">{a.name || "Achievement"}</h4>
                            <p className="text-xs text-gray-600 leading-relaxed">{a.description || "Achievement description"}</p>
                          </div>
                          <div className="text-xs text-gray-500">
                            Earned {a.earnedDate ? new Date(a.earnedDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : "Recently"}
                          </div>
                        </div>
                      ))}
                    </div>
                  </UserQuickViewSection>
                )}
              </>
            )}

            {([UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER] as UserRole[]).includes(currentUserRole!) && (
              <UserQuickViewSection title="Administrative Actions">
                <div className="space-y-4">
                  <ToggleRow
                    title="Email Notifications"
                    label={user.email_notifications_enabled ? "Enabled" : "Disabled"}
                    value={user.email_notifications_enabled}
                    onChange={handleNotificationToggle}
                    disabled={user.role === UserRole.ADMIN && currentUserRole !== UserRole.ADMIN}
                    className="!bg-gray-50"
                  />
                  <p className="text-xs text-gray-500 px-1">
                    Toggle whether this user receives non-critical system and marketing emails.
                  </p>

                  <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 border border-gray-100">
                    <span className="text-sm font-medium text-gray-900">MFA</span>
                    <span className={`text-sm font-semibold ${user.totpConfirmedAt ? "text-green-600" : user.twoFaEnabled ? "text-blue-600" : "text-gray-500"}`}>
                      {user.totpConfirmedAt ? "MFA (TOTP)" : user.twoFaEnabled ? "Email Only" : "Disabled"}
                    </span>
                  </div>

                  {isBusinessRole && (
                    <div className="pt-2 border-t border-gray-100">
                      <ToggleRow
                        title="SOW Scope Intake"
                        label={!user.sowExempt ? "Required" : "Not Required (Default)"}
                        value={!user.sowExempt}
                        onChange={handleSOWToggle}
                        className="!bg-gray-50"
                      />
                      <p className="text-xs text-gray-500 px-1 mt-1">
                        Turn on to require this client to complete the initial SOW scope questionnaire gating screen.
                      </p>
                    </div>
                  )}
                </div>
              </UserQuickViewSection>
            )}
          </div>
        </div>
      </div>
    </QuickViewModal >
  );
};

export default UserQuickView;
